import React, { useEffect, useState } from 'react';

import { API_CASINO_NEWS_WIDGET_GAMES_ADD, API_CASINO_NEWS_WIDGET_GAMES_DELETE } from '../Helpers/CrmApiUrls';
import { CRM_PERMISSION } from '../Helpers/CrmPermissions';

import CasinoCRMApi from 'casino/Api/CRM/CasinoCRMApi';
import { CasinoNewsGamesWidgetResponse } from 'casino/Api/CRM/Responses/CasinoNewsGamesWidgetResponse';
import CasinoGame, { CasinoGameDeviceType } from 'casino/Api/Models/CasinoGame';
import GameSearchInput from 'crm/casino/elements/GameSearchInput';
import EditableGameList from 'crm/casino/elements/Games/EditableGameList';
import Search from 'crm/elements/Search';
import Card from 'crm/elements/common/Card';
import TableLoader from 'crm/elements/common/TableLoader';
import Paginator, { PaginatorProps } from 'crm/elements/paginator';
import useAutoPaginator from 'crm/hooks/useAutoPaginator';

const NewsGamesWidget = () => {
    const {
        items: desktopNewsGamesWidgetGames,
        updateItems: updateDesktopNewsGamesWidgetGames,
        isLoading: isLoadingDesktopNewsGamesWidgetGames,
        auto: autoDesktopNewsGamesWidgetGames,
    } = useAutoPaginator<CasinoNewsGamesWidgetResponse>((page, params) => {
        return CasinoCRMApi.newsGamesWidget.getWithGames(page, CasinoGameDeviceType.DESKTOP, { params });
    });

    const {
        items: mobileNewsGamesWidgetGames,
        updateItems: updateMobileNewsGamesWidgetGames,
        isLoading: isLoadingMobileNewsGamesWidgetGames,
        auto: autoMobileNewsGamesWidgetGames,
    } = useAutoPaginator<CasinoNewsGamesWidgetResponse>((page, params) => {
        return CasinoCRMApi.newsGamesWidget.getWithGames(page, CasinoGameDeviceType.MOBILE, { params });
    });
    const [desktopGamesList, setDesktopGamesList] = useState<CasinoGame[]>([]);
    const [mobileGamesList, setMobileGamesList] = useState<CasinoGame[]>([]);

    useEffect(() => {
        const extractedGames = desktopNewsGamesWidgetGames.map((game) => game.casino_game);
        setDesktopGamesList(extractedGames);
    }, [desktopNewsGamesWidgetGames]);

    useEffect(() => {
        const extractedGames = mobileNewsGamesWidgetGames.map((game) => game.casino_game);
        setMobileGamesList(extractedGames);
    }, [mobileNewsGamesWidgetGames]);

    const handleDeleteCallback = async (game: CasinoGame) => {
        if (game.is_desktop) {
            updateDesktopNewsGamesWidgetGames();
        }

        if (game.is_mobile) {
            updateMobileNewsGamesWidgetGames();
        }
    };

    const refreshGames = () => {
        updateDesktopNewsGamesWidgetGames();
        updateMobileNewsGamesWidgetGames();
    };

    const generateTable = (
        deviceType: CasinoGameDeviceType,
        games: CasinoGame[],
        isLoading: boolean,
        paginator: PaginatorProps,
        search: {
            filterTable: (value: string) => void;
        }
    ) => {
        return (
            <>
                <Card title={<h4 className="card-title text-capitalize font-weight-bold">{deviceType} Games</h4>}>
                    <Search {...search} />
                    <TableLoader isLoading={isLoading}>
                        <EditableGameList
                            deleteUrl={API_CASINO_NEWS_WIDGET_GAMES_DELETE}
                            games={games}
                            deleteCallback={handleDeleteCallback}
                            permissions={[CRM_PERMISSION.NEWS_GAMES_WIDGET_DELETE]}
                        />
                    </TableLoader>
                    <Paginator {...paginator} />
                </Card>
            </>
        );
    };

    const getContent = () => {
        return (
            <>
                <GameSearchInput
                    action={refreshGames}
                    allowedTypes={null}
                    liveOnly={false}
                    insertUrl={API_CASINO_NEWS_WIDGET_GAMES_ADD}
                />
                {generateTable(
                    CasinoGameDeviceType.DESKTOP,
                    desktopGamesList,
                    isLoadingDesktopNewsGamesWidgetGames,
                    autoDesktopNewsGamesWidgetGames.paginator,
                    autoDesktopNewsGamesWidgetGames.search
                )}
                {generateTable(
                    CasinoGameDeviceType.MOBILE,
                    mobileGamesList,
                    isLoadingMobileNewsGamesWidgetGames,
                    autoMobileNewsGamesWidgetGames.paginator,
                    autoMobileNewsGamesWidgetGames.search
                )}
            </>
        );
    };

    return getContent();
};

export default NewsGamesWidget;
